import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Paper,
  CircularProgress,
  Autocomplete,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  PhotoLibrary as GalleryIcon,
  Add as AddIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useInventoryContext } from '../contexts/InventoryContext';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { FEATURE_LIMITS } from '../constants/featureLimits';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useKeyboardScroll } from '../hooks/useKeyboardScroll';
import { useSafeArea } from '../hooks/useSafeArea';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

export const InventoryForm: React.FC = () => {
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const { createItem, updateItem, getItemById, folders } = useInventoryContext();
  const { user } = useAuth();
  const safeArea = useSafeArea();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!itemId);

  // State for Photo Dialog
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [activePhotoField, setActivePhotoField] = useState<'photoUrl' | 'photoUrl2' | null>(null);

  const [formData, setFormData] = useState({
    itemName: '',
    description: '',
    quantity: '1',
    store: '',
    unitValue: '',
    category: '',
    brand: '',
    measurements: '',
    observations: '',
    photoUrl: '',
    photoUrl2: '',
    folderId: '',
  });


  // Categorias pré-definidas
  const predefinedCategories = [
    'Pratos',
    'Copos',
    'Taças',
    'Xícaras',
    'Talheres',
    'Tigelas',
    'Travessas',
    'Panelas',
    'Jogos Americanos',
    'Guardanapos',
    'Toalhas de Mesa',
    'Decoração',
    'Utensílios',
    'Eletrodomésticos',
    'Outro',
  ];

  // Pegar categorias customizadas dos itens existentes
  const { getStats } = useInventoryContext();
  const stats = getStats();
  const customCategoriesFromItems = stats.categories.filter(
    cat => !predefinedCategories.includes(cat) && cat !== ''
  );

  // Combinar categorias
  const allCategories = [...predefinedCategories, ...customCategoriesFromItems];

  // Verificar limite de categorias customizadas
  const { plan } = useSubscription();
  const planLimits = FEATURE_LIMITS[plan] || FEATURE_LIMITS.free;
  const customCategoryLimit = planLimits.customCategories;
  const canAddCustomCategory = customCategoryLimit === -1 || customCategoriesFromItems.length < customCategoryLimit;

  const isEditMode = !!itemId;
  const isQuickMode = searchParams.get('quick') === 'true';

  // Hook para scroll automático quando teclado aparecer (Capacitor)
  useKeyboardScroll();

  // Carregar dados do item se estiver em modo de edição
  useEffect(() => {
    if (itemId) {
      const item = getItemById(itemId);
      if (item) {
        setFormData({
          itemName: item.itemName || '',
          description: item.description || '',
          quantity: item.quantity?.toString() || '1',
          store: item.store || '',
          unitValue: item.unitValue?.toString() || '',
          category: item.category || '',
          brand: item.brand || '',
          measurements: item.measurements || '',
          observations: item.observations || '',
          photoUrl: item.photoUrl || '',
          photoUrl2: (item as any).photoUrl2 || '',
          folderId: item.folderId || '',
        });
      } else {
        alert('Item não encontrado');
        navigate('/inventory');
      }
      setInitialLoading(false);
    }
  }, [itemId, getItemById, navigate]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (photoField: 'photoUrl' | 'photoUrl2', source: CameraSource) => {
    if (!user?.uid) return;

    // Fechar dialog
    setShowPhotoDialog(false);
    setActivePhotoField(null);

    setUploadingImage(true);
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: source,
      });

      if (!image.dataUrl) {
        setUploadingImage(false);
        return;
      }

      const response = await fetch(image.dataUrl);
      const blob = await response.blob();

      if (blob.size > 15 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 15MB');
        setUploadingImage(false);
        return;
      }

      const timestamp = Date.now();
      const filename = `${timestamp}_${image.format || 'jpg'}`;
      const storageRef = ref(storage, `inventory/${user.uid}/${filename}`);

      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);

      setFormData(prev => ({ ...prev, [photoField]: downloadURL }));
    } catch (error: any) {
      if (error.message !== 'User cancelled photos app') {
        console.error('Erro ao fazer upload:', error);
        alert('Erro ao fazer upload da imagem. Tente novamente.');
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageClick = () => {
    if (!uploadingImage) {
      setActivePhotoField('photoUrl');
      setShowPhotoDialog(true);
    }
  };

  const handleImageClick2 = () => {
    if (!uploadingImage) {
      setActivePhotoField('photoUrl2');
      setShowPhotoDialog(true);
    }
  };

  const handleRemoveImage = (photoField: 'photoUrl' | 'photoUrl2' = 'photoUrl') => {
    setFormData(prev => ({ ...prev, [photoField]: '' }));
  };

  const handleSubmit = async () => {
    if (!formData.itemName.trim()) {
      alert('Por favor, preencha o nome do item');
      return;
    }

    // Verificar se está criando uma nova categoria customizada
    const isNewCustomCategory = formData.category &&
      !predefinedCategories.includes(formData.category) &&
      !customCategoriesFromItems.includes(formData.category);

    if (isNewCustomCategory && !isEditMode && user?.uid) {
      // Importar dinamicamente para evitar circular dependency
      const { checkLimit } = await import('../services/limitsService');
      const limitCheck = await checkLimit('categories', user.uid);

      if (!limitCheck.allowed) {
        alert(limitCheck.message || 'Limite de categorias customizadas atingido');
        return;
      }
    }

    setLoading(true);
    try {
      const itemData = {
        itemName: formData.itemName.trim(),
        description: formData.description?.trim() || undefined,
        quantity: parseInt(formData.quantity) || 1,
        store: formData.store?.trim() || undefined,
        unitValue: formData.unitValue ? parseFloat(formData.unitValue) : undefined,
        category: formData.category?.trim() || undefined,
        brand: formData.brand?.trim() || undefined,
        measurements: formData.measurements?.trim() || undefined,
        observations: formData.observations?.trim() || undefined,
        photoUrl: formData.photoUrl || undefined,
        photoUrl2: formData.photoUrl2 || undefined,
        folderId: formData.folderId || undefined,
      };

      if (isEditMode && itemId) {
        // Atualizar item existente
        await updateItem(itemId, itemData);
        navigate(`/inventory/${itemId}`);
      } else {
        // Criar novo item
        const newItem = await createItem(itemData);

        // Incrementar contador de categoria customizada se for nova
        if (isNewCustomCategory && user?.uid) {
          const { incrementCustomCategoryCount } = await import('../services/userRecordsService');
          await incrementCustomCategoryCount(user.uid);
        }

        navigate(`/inventory/${newItem.id}`);
      }
    } catch (error: any) {
      console.error('Erro ao salvar item:', error);

      let errorMessage = `Erro ao ${isEditMode ? 'atualizar' : 'criar'} item. Tente novamente.`;

      if (error.message?.includes('Limite')) {
        errorMessage = error.message;
      } else if (error.message?.includes('autenticado')) {
        errorMessage = 'Você precisa estar autenticado para salvar itens.';
      } else if (error.message?.includes('permissão')) {
        errorMessage = 'Você não tem permissão para salvar itens.';
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <>
        <TopBar
          title={isEditMode ? "Editar Item" : "Novo Item"}
          showBackButton
          onBackClick={() => navigate(-1)}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <TopBar
        title={isEditMode ? "Editar Item" : isQuickMode ? "Cadastro Rápido" : "Novo Item"}
        showBackButton
        onBackClick={() => navigate(-1)}
      />
      <Box sx={{
        paddingTop: '70px',
        paddingBottom: '120px',
        maxWidth: '600px',
        margin: '0 auto',
        px: 3,
        minHeight: '100vh',
      }}>
        {/* Modo Rápido */}
        {isQuickMode ? (
          <>
            <TextField
              fullWidth
              label="Nome do Item *"
              value={formData.itemName}
              onChange={(e) => handleChange('itemName', e.target.value)}
              disabled={loading}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                }
              }}
            />

            <TextField
              fullWidth
              label="Quantidade"
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              disabled={loading}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                }
              }}
              slotProps={{
                input: {
                  inputProps: { min: 1 }
                }
              }}
            />
          </>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: theme.colors.text.secondary }}>
                  Foto 1
                </Typography>
                <Paper
                  elevation={0}
                  onClick={!uploadingImage ? handleImageClick : undefined}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px dashed ${theme.colors.border}`,
                    cursor: uploadingImage ? 'wait' : 'pointer',
                    position: 'relative',
                    backgroundImage: formData.photoUrl ? `url(${formData.photoUrl})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: uploadingImage ? theme.colors.border : theme.colors.primary,
                      backgroundColor: uploadingImage ? theme.colors.card : `${theme.colors.primary}05`,
                    }
                  }}
                >
                  {uploadingImage ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress sx={{ color: theme.colors.primary }} />
                      <Typography sx={{ color: theme.colors.text.secondary, mt: 1 }}>
                        Fazendo upload...
                      </Typography>
                    </Box>
                  ) : !formData.photoUrl ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CameraIcon sx={{ fontSize: '3rem', color: theme.colors.text.secondary, opacity: 0.5 }} />
                      <Typography sx={{ color: theme.colors.text.secondary, mt: 1 }}>
                        Adicionar foto
                      </Typography>
                    </Box>
                  ) : null}

                  {formData.photoUrl && !uploadingImage && (
                    <IconButton
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleRemoveImage('photoUrl');
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: theme.colors.text.secondary }}>
                  Foto 2 (Opcional)
                </Typography>
                <Paper
                  elevation={0}
                  onClick={!uploadingImage ? handleImageClick2 : undefined}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    backgroundColor: theme.colors.card,
                    borderRadius: theme.radii.medium,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px dashed ${theme.colors.border}`,
                    cursor: uploadingImage ? 'wait' : 'pointer',
                    position: 'relative',
                    backgroundImage: formData.photoUrl2 ? `url(${formData.photoUrl2})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: uploadingImage ? theme.colors.border : theme.colors.primary,
                      backgroundColor: uploadingImage ? theme.colors.card : `${theme.colors.primary}05`,
                    }
                  }}
                >
                  {uploadingImage ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress sx={{ color: theme.colors.primary }} />
                      <Typography sx={{ color: theme.colors.text.secondary, mt: 1 }}>
                        Fazendo upload...
                      </Typography>
                    </Box>
                  ) : !formData.photoUrl2 ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CameraIcon sx={{ fontSize: '3rem', color: theme.colors.text.secondary, opacity: 0.5 }} />
                      <Typography sx={{ color: theme.colors.text.secondary, mt: 1 }}>
                        Adicionar foto
                      </Typography>
                    </Box>
                  ) : null}

                  {formData.photoUrl2 && !uploadingImage && (
                    <IconButton
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleRemoveImage('photoUrl2');
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Nome do Item *"
              value={formData.itemName}
              onChange={(e) => handleChange('itemName', e.target.value)}
              disabled={loading}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                }
              }}
            />

            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={loading}
              multiline
              rows={3}
              placeholder="Ex: Prato com flores vermelhas e borda trabalhada"
              slotProps={{
                inputLabel: {
                  shrink: true,
                }
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                }
              }}
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Quantidade"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: theme.radii.medium,
                    }
                  }}
                  slotProps={{
                    input: {
                      inputProps: { min: 1 }
                    }
                  }}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Onde comprei"
                  value={formData.store}
                  onChange={(e) => handleChange('store', e.target.value)}
                  disabled={loading}
                  placeholder="Ex: Camicado"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: theme.radii.medium,
                    }
                  }}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Valor unitário"
                  type="number"
                  value={formData.unitValue}
                  onChange={(e) => handleChange('unitValue', e.target.value)}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: theme.radii.medium,
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                      inputProps: { min: 0, step: 0.01 }
                    }
                  }}
                />
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  freeSolo={canAddCustomCategory}
                  options={allCategories}
                  value={formData.category || ''}
                  onChange={(_, newValue) => {
                    handleChange('category', newValue || '');
                  }}
                  onInputChange={(_, newInputValue) => {
                    // Só permite digitar se pode adicionar categoria customizada OU se é categoria existente
                    if (canAddCustomCategory || allCategories.includes(newInputValue)) {
                      handleChange('category', newInputValue);
                    }
                  }}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categoria"
                      placeholder={canAddCustomCategory ? "Digite ou selecione" : "Selecione uma categoria"}
                      helperText={!canAddCustomCategory && customCategoryLimit === 0 ? "🔒 Categorias customizadas disponíveis apenas para planos pagos" : undefined}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: theme.radii.medium,
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {canAddCustomCategory && (
                              <InputAdornment position="end" sx={{ mr: 1 }}>
                                <AddIcon
                                  sx={{
                                    color: theme.colors.primary,
                                    fontSize: 20,
                                    opacity: 0.7,
                                  }}
                                />
                              </InputAdornment>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Marca"
                  value={formData.brand}
                  onChange={(e) => handleChange('brand', e.target.value)}
                  disabled={loading}
                  placeholder="Ex: Porto Brasil"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: theme.radii.medium,
                    }
                  }}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Medidas"
                  value={formData.measurements}
                  onChange={(e) => handleChange('measurements', e.target.value)}
                  disabled={loading}
                  placeholder="Ex: 19 cm"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: theme.radii.medium,
                    }
                  }}
                />
              </Grid>
            </Grid>

            <FormControl
              fullWidth
              disabled={loading}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                },
              }}
            >
              <InputLabel id="folder-select-label">Pasta</InputLabel>
              <Select
                labelId="folder-select-label"
                label="Pasta"
                value={folders.some((folder) => folder.id === formData.folderId) ? formData.folderId : ''}
                onChange={(e) => handleChange('folderId', e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FolderIcon sx={{ color: theme.colors.text.secondary, ml: 1 }} fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="">
                  <em>Nenhuma (raiz)</em>
                </MenuItem>
                {folders.map((folder) => (
                  <MenuItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Observações"
              value={formData.observations}
              onChange={(e) => handleChange('observations', e.target.value)}
              disabled={loading}
              multiline
              rows={4}
              placeholder="Adicione observações sobre este item..."
              slotProps={{
                inputLabel: {
                  shrink: true,
                }
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                }
              }}
            />
          </>
        )}

        {/* Botões de ação */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={loading || uploadingImage}
            sx={{
              borderColor: theme.colors.border,
              color: theme.colors.text.primary,
              borderRadius: theme.radii.medium,
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                borderColor: theme.colors.primary,
                backgroundColor: `${theme.colors.primary}05`,
              }
            }}
          >
            Cancelar
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || uploadingImage || !formData.itemName.trim()}
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              borderRadius: theme.radii.medium,
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              boxShadow: theme.shadows.medium,
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.9,
              }
            }}
          >
            {loading ? (isEditMode ? 'Salvando...' : 'Criando...') : uploadingImage ? 'Aguarde...' : (isEditMode ? 'Salvar Alterações' : 'Criar Item')}
          </Button>
        </Box>
      </Box>
      <BottomNavigation />

      <Drawer
        anchor="bottom"
        open={showPhotoDialog}
        onClose={() => setShowPhotoDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        }}
      >
        <Box sx={{ p: 2, pb: `${safeArea.bottom + 16}px` }}>
          <Box sx={{
            backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: theme.radii.large,
            overflow: 'hidden',
            mb: 1,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border}`, display: 'flex', justifyContent: 'center' }}>
              <Typography variant="subtitle2" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                Adicionar Foto
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              <ListItem
                onClick={() => activePhotoField && handleImageUpload(activePhotoField, CameraSource.Camera)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  borderBottom: `1px solid ${theme.colors.border}`,
                  '&:active': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.colors.primary }}>
                  <CameraIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Tirar Foto"
                  primaryTypographyProps={{
                    sx: {
                      color: theme.colors.primary,
                      fontWeight: 600,
                      fontSize: '17px',
                      textAlign: 'center',
                      ml: -5
                    }
                  }}
                />
              </ListItem>
              <ListItem
                onClick={() => activePhotoField && handleImageUpload(activePhotoField, CameraSource.Photos)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  '&:active': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.colors.primary }}>
                  <GalleryIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Escolher da Galeria"
                  primaryTypographyProps={{
                    sx: {
                      color: theme.colors.primary,
                      fontWeight: 600,
                      fontSize: '17px',
                      textAlign: 'center',
                      ml: -5
                    }
                  }}
                />
              </ListItem>
            </List>
          </Box>

          <Button
            fullWidth
            onClick={() => setShowPhotoDialog(false)}
            sx={{
              backgroundColor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: theme.radii.large,
              py: 2,
              color: '#007AFF', // iOS blue
              fontWeight: 600,
              fontSize: '17px',
              textTransform: 'none',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(40, 40, 40, 0.95)' : 'rgba(245, 245, 245, 0.95)',
              }
            }}
          >
            Cancelar
          </Button>
        </Box>
      </Drawer>
    </>
  );
};
